import React, { useState, useEffect } from 'react';
import { 
  CreditCard, CheckCircle, XCircle, AlertCircle, Loader2, 
  RefreshCw, Copy, ExternalLink, Zap, Check, Link2,
  Crown, Star, Home, ArrowRight, Info, Clock
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { useAdmin } from '../../context/AdminContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const WEBHOOK_URL = `${process.env.REACT_APP_BACKEND_URL}/api/webhook/stripe`;

const REQUIRED_ENV_VARS = [
  { key: 'STRIPE_SECRET_KEY', description: 'Your Stripe secret API key (starts with sk_)' },
  { key: 'STRIPE_PUBLISHABLE_KEY', description: 'Your Stripe publishable key (starts with pk_)' },
  { key: 'STRIPE_WEBHOOK_SECRET', description: 'Webhook signing secret (starts with whsec_)' }
];

const REQUIRED_WEBHOOK_EVENTS = [
  { event: 'checkout.session.completed', description: 'Subscription checkout completed' },
  { event: 'customer.subscription.created', description: 'New subscription created' },
  { event: 'customer.subscription.updated', description: 'Subscription plan changed' },
  { event: 'customer.subscription.deleted', description: 'Subscription canceled' },
  { event: 'invoice.paid', description: 'Monthly payment succeeded' },
  { event: 'invoice.payment_failed', description: 'Payment failed (triggers retry)' }
];

const TIER_ICONS = {
  standard: Home,
  featured: Star,
  concierge: Crown
};

const AdminPaymentsPage = () => {
  const { getStripeStatus, testStripeConnection, getWebhookLogs, getSubscriptionTiers, getSettings } = useAdmin();
  const navigate = useNavigate();
  
  const [stripeStatus, setStripeStatus] = useState(null);
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statusData, logsData, tiersData, settingsData] = await Promise.all([
        getStripeStatus(),
        getWebhookLogs(10),
        getSubscriptionTiers().catch(() => ({ tiers: [] })),
        getSettings().catch(() => ({}))
      ]);
      setStripeStatus(statusData);
      setWebhookLogs(logsData?.logs || []);
      setTiers(tiersData?.tiers || []);
      setSettings(settingsData);
    } catch (error) {
      toast.error('Failed to load payment settings');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await testStripeConnection();
      setTestResult(result);
      if (result.success) {
        toast.success('Stripe connection successful');
      } else {
        toast.error(result.message || 'Connection test failed');
      }
      // Refresh status after test
      const statusData = await getStripeStatus();
      setStripeStatus(statusData);
    } catch (error) {
      toast.error('Connection test failed');
      setTestResult({ success: false, message: 'Connection error' });
    } finally {
      setTesting(false);
    }
  };

  const handleCopy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  // Calculate wizard step status
  const getStepStatus = () => {
    const keysPresent = stripeStatus?.keys_present;
    const webhookConfigured = stripeStatus?.webhook_configured;
    const connectionVerified = testResult?.success || stripeStatus?.last_test_result === 'success';
    
    return {
      step1: keysPresent ? 'complete' : 'pending',
      step2: webhookConfigured ? 'complete' : keysPresent ? 'pending' : 'disabled',
      step3: connectionVerified ? 'complete' : webhookConfigured ? 'pending' : 'disabled'
    };
  };

  const stepStatus = getStepStatus();

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-trust-blue" />
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="admin-payments">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-white mb-2">
          Payments (Stripe)
        </h1>
        <p className="text-slate-400">
          Stripe is used to bill monthly subscription tiers for property owners.
        </p>
      </div>

      {/* ==================== STRIPE SETUP WIZARD ==================== */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 mb-6">
        <h2 className="font-heading text-xl font-semibold text-white mb-2 flex items-center gap-2">
          <Zap className="w-5 h-5 text-trust-blue" />
          Stripe Setup Wizard
        </h2>
        <p className="text-slate-400 text-sm mb-6">
          Follow these 3 steps to connect Stripe for owner subscriptions.
        </p>

        <div className="space-y-6">
          {/* Step 1: Add Stripe Keys */}
          <div className={`rounded-lg border p-5 ${
            stepStatus.step1 === 'complete' ? 'border-green-500/50 bg-green-500/5' : 'border-slate-600 bg-slate-700/30'
          }`}>
            <div className="flex items-start gap-4">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                stepStatus.step1 === 'complete' ? 'bg-green-500' : 'bg-slate-600'
              }`}>
                {stepStatus.step1 === 'complete' ? (
                  <Check className="w-4 h-4 text-white" />
                ) : (
                  <span className="text-white font-medium text-sm">1</span>
                )}
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-white mb-1">Add Stripe Keys</h3>
                <p className="text-slate-400 text-sm mb-4">
                  Configure your Stripe API keys as environment variables.
                </p>

                {/* Env Var Checklist */}
                <div className="space-y-3 mb-4">
                  {REQUIRED_ENV_VARS.map((envVar) => {
                    const isPresent = envVar.key === 'STRIPE_SECRET_KEY' ? stripeStatus?.keys_present :
                                      envVar.key === 'STRIPE_WEBHOOK_SECRET' ? stripeStatus?.webhook_configured :
                                      stripeStatus?.keys_present; // Assume publishable is set with secret
                    
                    return (
                      <div key={envVar.key} className="flex items-center justify-between bg-slate-800 rounded-lg p-3">
                        <div className="flex items-center gap-3">
                          {isPresent ? (
                            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                          )}
                          <div>
                            <code className="text-trust-blue text-sm font-mono">{envVar.key}</code>
                            <p className="text-xs text-slate-500">{envVar.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={isPresent ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                            {isPresent ? 'Present' : 'Missing'}
                          </Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-slate-400 hover:text-white h-8 w-8 p-0"
                            onClick={() => handleCopy(envVar.key, envVar.key)}
                            title="Copy variable name"
                          >
                            {copiedKey === envVar.key ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Helper text */}
                <div className="bg-slate-900/50 rounded-lg p-3 flex items-start gap-2">
                  <Info className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-slate-400">
                    <strong className="text-slate-300">Where to add:</strong>{' '}
                    Emergent → Deployments → Environment Variables (Backend)
                    <br />
                    <a 
                      href="https://dashboard.stripe.com/apikeys" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-trust-blue hover:underline inline-flex items-center gap-1 mt-1"
                    >
                      Get keys from Stripe Dashboard <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Step 2: Configure Webhook */}
          <div className={`rounded-lg border p-5 ${
            stepStatus.step2 === 'complete' ? 'border-green-500/50 bg-green-500/5' :
            stepStatus.step2 === 'disabled' ? 'border-slate-700 bg-slate-800/30 opacity-60' :
            'border-slate-600 bg-slate-700/30'
          }`}>
            <div className="flex items-start gap-4">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                stepStatus.step2 === 'complete' ? 'bg-green-500' :
                stepStatus.step2 === 'disabled' ? 'bg-slate-700' : 'bg-slate-600'
              }`}>
                {stepStatus.step2 === 'complete' ? (
                  <Check className="w-4 h-4 text-white" />
                ) : (
                  <span className="text-white font-medium text-sm">2</span>
                )}
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-white mb-1">Configure Webhook</h3>
                <p className="text-slate-400 text-sm mb-4">
                  Set up Stripe to send subscription events to your app.
                </p>

                {/* Webhook URL */}
                <div className="mb-4">
                  <label className="text-sm text-slate-400 mb-2 block">Webhook Endpoint URL</label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-slate-800 rounded-lg p-3 font-mono text-sm text-slate-300 overflow-x-auto">
                      {WEBHOOK_URL}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-slate-600 text-slate-300 hover:text-white flex-shrink-0"
                      onClick={() => handleCopy(WEBHOOK_URL, 'webhook-url')}
                    >
                      {copiedKey === 'webhook-url' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      <span className="ml-2">Copy URL</span>
                    </Button>
                  </div>
                </div>

                {/* Required Events */}
                <div className="mb-4">
                  <label className="text-sm text-slate-400 mb-2 block">Required Webhook Events</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {REQUIRED_WEBHOOK_EVENTS.map((item) => (
                      <div key={item.event} className="flex items-center gap-2 bg-slate-800 rounded-lg px-3 py-2">
                        <Link2 className="w-4 h-4 text-slate-500" />
                        <div>
                          <code className="text-xs text-amber-400">{item.event}</code>
                          <p className="text-xs text-slate-500">{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Webhook Status */}
                <div className="flex items-center gap-3 bg-slate-800 rounded-lg p-3">
                  <span className="text-sm text-slate-400">Webhook Configured:</span>
                  <Badge className={stripeStatus?.webhook_configured ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'}>
                    {stripeStatus?.webhook_configured ? 'Yes' : 'No'}
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Step 3: Verify Connection */}
          <div className={`rounded-lg border p-5 ${
            stepStatus.step3 === 'complete' ? 'border-green-500/50 bg-green-500/5' :
            stepStatus.step3 === 'disabled' ? 'border-slate-700 bg-slate-800/30 opacity-60' :
            'border-slate-600 bg-slate-700/30'
          }`}>
            <div className="flex items-start gap-4">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                stepStatus.step3 === 'complete' ? 'bg-green-500' :
                stepStatus.step3 === 'disabled' ? 'bg-slate-700' : 'bg-slate-600'
              }`}>
                {stepStatus.step3 === 'complete' ? (
                  <Check className="w-4 h-4 text-white" />
                ) : (
                  <span className="text-white font-medium text-sm">3</span>
                )}
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-white mb-1">Verify Connection</h3>
                <p className="text-slate-400 text-sm mb-4">
                  Test that Stripe can communicate with your app.
                </p>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <Button
                    onClick={handleTestConnection}
                    disabled={testing || !stripeStatus?.keys_present}
                    variant="outline"
                    className="border-slate-600 text-slate-300 hover:text-white hover:bg-slate-700"
                    data-testid="test-stripe-connection"
                  >
                    {testing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Test Stripe Connection
                  </Button>

                  <a 
                    href="https://dashboard.stripe.com/webhooks" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-trust-blue hover:underline"
                  >
                    Test via Stripe Dashboard <ExternalLink className="w-3 h-3" />
                  </a>
                </div>

                {/* Test Result */}
                {testResult && (
                  <div className={`flex items-center gap-2 p-3 rounded-lg ${
                    testResult.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'
                  }`}>
                    {testResult.success ? (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-400" />
                    )}
                    <span className={testResult.success ? 'text-green-400' : 'text-red-400'}>
                      {testResult.message}
                    </span>
                    {testResult.test_mode && (
                      <Badge className="bg-amber-500/20 text-amber-400 ml-2">Test Mode</Badge>
                    )}
                  </div>
                )}

                {/* Mode Indicator */}
                {stripeStatus?.keys_present && (
                  <div className="mt-3 p-3 bg-slate-800 rounded-lg flex items-center gap-2">
                    <Zap className={`w-4 h-4 ${stripeStatus?.test_mode ? 'text-amber-400' : 'text-green-400'}`} />
                    <span className="text-sm text-slate-400">
                      Stripe Mode: <span className={stripeStatus?.test_mode ? 'text-amber-400' : 'text-green-400'}>
                        {stripeStatus?.test_mode ? 'Test' : 'Live'}
                      </span>
                    </span>
                    {stripeStatus?.test_mode && (
                      <span className="text-xs text-slate-500">(No real charges will be made)</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ==================== SUBSCRIPTION BILLING SETTINGS ==================== */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 mb-6">
        <h2 className="font-heading text-xl font-semibold text-white mb-2 flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-trust-blue" />
          Subscription Billing Settings (Owners)
        </h2>
        <p className="text-slate-400 text-sm mb-6">
          Stay Pair Homes charges property owners a monthly subscription fee. There are no per-booking fees.
        </p>

        {/* Global Toggle */}
        <div className="flex items-center justify-between bg-slate-700/50 rounded-lg p-4 mb-6">
          <div>
            <p className="font-medium text-white">Subscriptions Enabled</p>
            <p className="text-sm text-slate-400">Allow owners to subscribe and list properties</p>
          </div>
          <div className="flex items-center gap-3">
            <Badge className={settings?.subscriptions_enabled !== false ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
              {settings?.subscriptions_enabled !== false ? 'Active' : 'Disabled'}
            </Badge>
            <Switch
              checked={settings?.subscriptions_enabled !== false}
              disabled
              className="opacity-50"
            />
          </div>
        </div>

        {/* Mode Indicator */}
        <div className="flex items-center gap-3 bg-slate-700/30 rounded-lg p-4 mb-6">
          <span className="text-slate-400 text-sm">Stripe Subscriptions Mode:</span>
          <Badge className={stripeStatus?.test_mode ? 'bg-amber-500/20 text-amber-400' : 'bg-green-500/20 text-green-400'}>
            {stripeStatus?.test_mode ? 'Test' : 'Live'}
          </Badge>
          <span className="text-xs text-slate-500">
            (Derived from Stripe API key type)
          </span>
        </div>

        {/* Tier Summary */}
        <div className="mb-6">
          <label className="text-sm text-slate-400 mb-3 block">Subscription Tiers (Read-only)</label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {tiers.length > 0 ? tiers.map((tier) => {
              const Icon = TIER_ICONS[tier.tier] || Home;
              return (
                <div key={tier.tier} className={`rounded-lg p-4 border ${
                  tier.enabled !== false ? 'bg-slate-700/30 border-slate-600' : 'bg-slate-800/30 border-slate-700 opacity-60'
                }`}>
                  <div className="flex items-center gap-3 mb-2">
                    <Icon className={`w-5 h-5 ${
                      tier.tier === 'featured' ? 'text-amber-400' : 
                      tier.tier === 'concierge' ? 'text-trust-blue' : 'text-slate-400'
                    }`} />
                    <span className="font-medium text-white capitalize">{tier.name || tier.tier}</span>
                    {tier.enabled === false && (
                      <Badge className="bg-red-500/20 text-red-400 text-xs">Disabled</Badge>
                    )}
                  </div>
                  <p className="text-2xl font-bold text-white">${tier.price}<span className="text-sm font-normal text-slate-400">/month</span></p>
                </div>
              );
            }) : (
              <>
                <div className="rounded-lg p-4 bg-slate-700/30 border border-slate-600">
                  <div className="flex items-center gap-3 mb-2">
                    <Home className="w-5 h-5 text-slate-400" />
                    <span className="font-medium text-white">Standard</span>
                  </div>
                  <p className="text-2xl font-bold text-white">$99<span className="text-sm font-normal text-slate-400">/month</span></p>
                </div>
                <div className="rounded-lg p-4 bg-slate-700/30 border border-slate-600">
                  <div className="flex items-center gap-3 mb-2">
                    <Star className="w-5 h-5 text-amber-400" />
                    <span className="font-medium text-white">Featured</span>
                  </div>
                  <p className="text-2xl font-bold text-white">$149<span className="text-sm font-normal text-slate-400">/month</span></p>
                </div>
                <div className="rounded-lg p-4 bg-slate-700/30 border border-slate-600">
                  <div className="flex items-center gap-3 mb-2">
                    <Crown className="w-5 h-5 text-trust-blue" />
                    <span className="font-medium text-white">Concierge</span>
                  </div>
                  <p className="text-2xl font-bold text-white">$199<span className="text-sm font-normal text-slate-400">/month</span></p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Link to Subscriptions Page */}
        <Button
          onClick={() => navigate('/admin/subscriptions')}
          className="bg-trust-blue hover:bg-trust-blue-hover text-white"
        >
          Go to Subscriptions Page to Edit Pricing
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>

      {/* ==================== RECENT WEBHOOK EVENTS ==================== */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-semibold text-white flex items-center gap-2">
            <Clock className="w-5 h-5 text-trust-blue" />
            Recent Webhook Deliveries
          </h2>
          <Button
            size="sm"
            variant="outline"
            className="border-slate-600 text-slate-300 hover:text-white"
            onClick={fetchData}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {webhookLogs.length === 0 ? (
          <div className="text-center py-8">
            <AlertCircle className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No webhook events received yet.</p>
            <p className="text-sm text-slate-500 mt-1">
              Events will appear here once Stripe sends subscription updates.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left text-slate-400 font-medium py-3 px-4">Timestamp</th>
                  <th className="text-left text-slate-400 font-medium py-3 px-4">Event Type</th>
                  <th className="text-left text-slate-400 font-medium py-3 px-4">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {webhookLogs.slice(0, 10).map((log, idx) => (
                  <tr key={log.event_id || idx} className="hover:bg-slate-700/30">
                    <td className="py-3 px-4 text-slate-300 text-sm">
                      {log.received_at ? format(new Date(log.received_at), 'MMM d, HH:mm:ss') : 'N/A'}
                    </td>
                    <td className="py-3 px-4">
                      <code className="text-xs bg-slate-700 px-2 py-1 rounded text-amber-400">
                        {log.event_type || 'unknown'}
                      </code>
                    </td>
                    <td className="py-3 px-4">
                      <Badge className={
                        log.status === 'success' ? 'bg-green-500/20 text-green-400' :
                        log.status === 'error' ? 'bg-red-500/20 text-red-400' :
                        'bg-amber-500/20 text-amber-400'
                      }>
                        {log.status || 'pending'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ==================== OPTIONAL BOOKING POLICIES (FUTURE) ==================== */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-slate-500 mt-0.5" />
          <div>
            <h3 className="font-medium text-slate-400 mb-1">Optional Booking Policies (Future)</h3>
            <p className="text-sm text-slate-500">
              Features like security deposit holds for renters are planned for a future release. 
              These do not affect owner subscription billing.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPaymentsPage;
